import { Dispatch, FunctionComponent, SetStateAction } from "react";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { Spinner } from "@/components/Spinner/Spinner";
import { Toggle } from "@/components/Toggle/Toggle";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { prospectJobTitle } from "@/utils/prospectJobTitle";

import { TrackerOrganization } from "../../schema/trackerOrganizations";
import { TrackerPerson } from "../../schema/trackerPerson";

interface ProfilesTabProps {
	trackerOrganization?: TrackerOrganization;
	failedProfileImages: Set<number>;
	setFailedProfileImages: Dispatch<SetStateAction<Set<number>>>;
	removePersonIsLoading: boolean;
	selectedTrackerPerson?: TrackerPerson;
	removePerson: (person: TrackerPerson) => void;
	addProspectIsLoading: boolean;
	togglingProspectId: number | null;
	handleAddProspectToTrackerOrganization: (prospectId: number) => void;
	setShowSearchPerson: (value: boolean) => void;
}

export const ProfilesTab: FunctionComponent<ProfilesTabProps> = ({
	trackerOrganization,
	failedProfileImages,
	setFailedProfileImages,
	removePersonIsLoading,
	selectedTrackerPerson,
	removePerson,
	addProspectIsLoading,
	togglingProspectId,
	handleAddProspectToTrackerOrganization,
	setShowSearchPerson,
}) => {
	const { t } = useTranslation();

	return (
		<div className="mb-8 flex flex-col items-end">
			<div className="max-w-[150px] mb-4">
				<Button
					size={ButtonSize.ML}
					title={t("list.addPerson")}
					onClick={() => setShowSearchPerson(true)}
				/>
			</div>
			<div className="grid grid-cols-1 gap-4 w-full">
				{trackerOrganization?.trackerPersons?.map((person, index) => (
					<div
						key={index}
						className="flex flex-row border justify-between border-gray-200 rounded-xl w-full items-center"
					>
						<div className="flex flex-row w-full items-center">
							{person?.prospect?.image &&
								person.prospect?.id &&
								!failedProfileImages.has(person.prospect.id) && (
									<img
										className="w-[48px] h-[48px] flex rounded-l-xl"
										src={person.prospect.image}
										onError={() => {
											if (person.prospect?.id) {
												setFailedProfileImages((prev) => new Set([...prev, person.prospect!.id]));
											}
										}}
									/>
								)}
							<div className="flex flex-col ml-3">
								<span className="text-sm font-semibold">{person.prospect?.name}</span>
								<span className="text-xs">{prospectJobTitle(person.prospect)}</span>
							</div>
							<div className="w-[60px] flex flex-col ml-3">
								{person.prospect?.linkedinUrl && (
									<Link target="_blank" to={person.prospect?.linkedinUrl}>
										<LinkedinIcon status="checked" />
									</Link>
								)}
							</div>
						</div>
						<div className="w-[60px] items-center flex justify-center ml-3 mr-2">
							{removePersonIsLoading && selectedTrackerPerson?.id === person.id ? (
								<Spinner color="#4d66f5" />
							) : (
								<Tooltip content={"Toggle off to remove from tracker"}>
									<Toggle isChecked={true} onChange={() => removePerson(person)} />
								</Tooltip>
							)}
						</div>
					</div>
				))}
				{trackerOrganization?.organization?.prospects
					?.filter((prospect) => {
						const trackerPersonIds = trackerOrganization?.trackerPersons?.map(
							(tp) => tp.prospect?.id
						);

						return !trackerPersonIds?.includes(prospect.id);
					})
					?.map((person, index) => (
						<div
							key={index}
							className="flex flex-row border justify-between border-gray-200 rounded-xl w-full items-center"
						>
							<div className="flex flex-row w-full items-center">
								{person?.image && !failedProfileImages.has(person.id) && (
									<img
										className="w-[48px] h-[48px] flex rounded-l-xl"
										src={person.image}
										onError={() => {
											setFailedProfileImages((prev) => new Set([...prev, person.id]));
										}}
									/>
								)}
								<div className="flex flex-col ml-3">
									<span className="text-sm font-semibold">{person?.name}</span>
									<span className="text-xs">{prospectJobTitle(person)}</span>
								</div>
								<div className="w-[60px] flex flex-col ml-3">
									{person?.linkedinUrl && (
										<Link target="_blank" to={person.linkedinUrl}>
											<LinkedinIcon status="checked" />
										</Link>
									)}
								</div>
							</div>
							<div className="w-[60px] items-center flex justify-center ml-3 mr-2">
								{addProspectIsLoading && togglingProspectId === person.id ? (
									<Spinner color="#4d66f5" />
								) : (
									<Tooltip content={"Toggle on to add to tracker"}>
										<Toggle
											isChecked={false}
											onChange={() => handleAddProspectToTrackerOrganization(person.id)}
										/>
									</Tooltip>
								)}
							</div>
						</div>
					))}
			</div>
		</div>
	);
};
